import cx from 'classnames';
import Image, { ImageProps } from 'next/image';
import { useIntl } from 'react-intl';

import { ampli } from '../../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../design-system/components/form/button/Button';
import { ImageCopyright } from '../../../../design-system/components/image/copyright/ImageCopyright';
import { ReadMore } from '../../../../design-system/components/read-more/ReadMore';
import { imageLoaderForPath } from '../../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../../resources/destination/domain/types/destination';
import { Facet } from '../../../../resources/facet/domain/types/facet';
import { useUrlGenerator } from '../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';

import style from './Cover.module.css';
import { CoverFormFacet } from './CoverFormFacet';

type Props = {
    image: ImageProps;
    copyright?: string;
    facet: PickDomain<Facet, 'filter' | 'title' | 'longTitle' | 'id' | 'description'>;
    destination: PickDomain<
        Destination,
        'id' | 'name' | 'slug' | 'isAvailableForQuoteRequest' | 'hasCommunityContent'
    >;
    trackingInformation: {
        tripFormContactAgency: TrackingProperties<typeof ampli.tripformCreationStartedFromFacet>;
    };
    destinationHasItinerary: boolean;
    isFamilyFacet: boolean;
};

export function Cover({
    image,
    copyright,
    facet,
    destination,
    trackingInformation,
    destinationHasItinerary,
    isFamilyFacet,
}: Props) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const { generatePath } = useUrlGenerator();

    return (
        <div className={style.cover}>
            <div className="imageOverlay">
                {copyright && copyright !== '' ? <ImageCopyright copyright={copyright} /> : null}
                <Image priority={true} {...image} alt={image.alt} fill className="imageCover" />
            </div>
            <div className={cx(style.layout, 'layout-with-centered-content')}>
                <div className="main-container">
                    <div className={cx(style.row, 'row')}>
                        <div
                            className={cx(
                                style.informationWrapper,
                                'col-12 col-lg-8 col-xl-7 col-xxl-6',
                            )}
                        >
                            <div className={style.information}>
                                <h1 className="font-secondary-m-bold mb-16">{facet.title}</h1>

                                {facet.description ? (
                                    <div className="mb-16">
                                        <ReadMore
                                            lineClamp={2}
                                            readMore={formatMessage({ id: 'global.cta.seeMore' })}
                                            readLess={formatMessage({ id: 'global.cta.seeLess' })}
                                            onToggle={(isOpen) => {
                                                trackEvent((ampli, defaultProperties) => {
                                                    if (isOpen) {
                                                        ampli.moreDetailsClicked({
                                                            ...defaultProperties,
                                                            block: 'cover',
                                                        });
                                                    }
                                                });
                                            }}
                                        >
                                            <p>{facet.description}</p>
                                        </ReadMore>
                                    </div>
                                ) : null}

                                {destination.isAvailableForQuoteRequest ? (
                                    <>
                                        <div className="hidden sm:!block">
                                            <div className="font-body-bold mb-16">
                                                {formatMessage({ id: 'page.facet.cover.quote' })}
                                            </div>
                                            <CoverFormFacet
                                                destination={destination}
                                                trackingInformation={trackingInformation}
                                                isFamilyFacet={isFamilyFacet}
                                            />
                                        </div>

                                        <Button
                                            className="sm:hidden"
                                            obfuscate
                                            isFullWidth
                                            onClick={() => {
                                                trackEvent((ampli, defaultProperties) => {
                                                    ampli.tripformCreationStartedFromFacet({
                                                        ...defaultProperties,
                                                        ...trackingInformation.tripFormContactAgency,
                                                    });
                                                });
                                            }}
                                            href={generatePath('trip_form', {
                                                destinationSlug: destination.slug,
                                            })}
                                        >
                                            {formatMessage({
                                                id: 'global.cta.tripForm.default',
                                            })}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="font-body-bold">
                                            {formatMessage({
                                                id: 'page.facet.coverFromDestinationNotAvailable.quote',
                                            })}
                                        </div>
                                        {destinationHasItinerary ? (
                                            <div className="mt-16 md:text-right">
                                                <Button
                                                    className={
                                                        style.fromDestinationNotAvailableButton
                                                    }
                                                    href={generatePath('itinerary_list', {
                                                        destinationSlug: destination.slug,
                                                    })}
                                                    onClick={() => {
                                                        trackEvent((ampli, defaultProperties) => {
                                                            ampli.itinerariesListClicked({
                                                                ...defaultProperties,
                                                                block: 'cartouche',
                                                                destination_id: destination.id,
                                                                destination_slug: destination.slug,
                                                            });
                                                        });
                                                    }}
                                                >
                                                    {formatMessage({
                                                        id: 'page.facet.coverFromDestinationNotAvailable.cta',
                                                    })}
                                                </Button>
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        </div>
                        {isFamilyFacet ? (
                            <div className="col-12 col-lg-4 col-xl-5 col-xxl-6">
                                <div className={style.familyLogoWrapper}>
                                    <Image
                                        loader={imageLoaderForPath}
                                        src="/logos/evaneos-family-white-logo.png"
                                        width={275}
                                        height={80}
                                        alt="Evaneos Family logo"
                                        priority
                                        sizes="(max-width: 767px) 180px, 275px"
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
